import { useState } from "react";
import { Project } from "@/integrations/supabase/types/";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  LayoutGrid, 
  CheckSquare, 
  FileText, 
  TrendingUp, 
  DollarSign, 
  Clock,
  Calendar,
  Edit,
  Trash2,
  Receipt
} from "lucide-react";
import { ProjectOverview } from "./project-details/ProjectOverview";
import { ProjectTasks } from "./project-details/ProjectTasks";
import { ProjectFiles } from "./project-details/ProjectFiles";
import { ProjectMilestones } from "./project-details/ProjectMilestones";
import { ProjectBudget } from "./project-details/ProjectBudget";
import { ProjectActivity } from "./project-details/ProjectActivity";
import { ProjectPayments } from "./project-details/ProjectPayments";
import { EditProjectDialog } from "./EditProjectDialog";

interface ProjectDetailsDialogProps {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export const ProjectDetailsDialog = ({
  project,
  open,
  onOpenChange,
  onUpdate,
}: ProjectDetailsDialogProps) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'planning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'on-hold':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <DialogTitle className="text-2xl mb-2">{project.name}</DialogTitle>
              <DialogDescription className="sr-only">
                Project details with tabs for overview, tasks, files, milestones, budget, and activity
              </DialogDescription>
              <div className="flex items-center gap-3">
                <Badge className={getStatusColor(project.status)}>
                  {project.status}
                </Badge>
                {project.due_date && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Due: {new Date(project.due_date).toLocaleDateString()}
                  </span>
                )}
                {project.budget && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Budget: ${project.budget.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => setEditDialogOpen(true)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <EditProjectDialog
              project={project}
              open={editDialogOpen}
              onOpenChange={setEditDialogOpen}
              onUpdate={onUpdate}
            />
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="overview" className="gap-2">
              <LayoutGrid className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <CheckSquare className="h-4 w-4" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="files" className="gap-2">
              <FileText className="h-4 w-4" />
              Files
            </TabsTrigger>
            <TabsTrigger value="milestones" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="budget" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Budget
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2">
              <Clock className="h-4 w-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2">
              <Receipt className="h-4 w-4" />
              Payments
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto py-6">
            <TabsContent value="overview" className="mt-0">
              <ProjectOverview project={project} onUpdate={onUpdate} />
            </TabsContent>

            <TabsContent value="tasks" className="mt-0">
              <ProjectTasks project={project} onUpdate={onUpdate} />
            </TabsContent>

            <TabsContent value="files" className="mt-0">
              <ProjectFiles project={project} onUpdate={onUpdate} />
            </TabsContent>

            <TabsContent value="milestones" className="mt-0">
              <ProjectMilestones project={project} onUpdate={onUpdate} />
            </TabsContent>

            <TabsContent value="budget" className="mt-0">
              <ProjectBudget project={project} onUpdate={onUpdate} />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <ProjectActivity project={project} />
            </TabsContent>

            <TabsContent value="payments" className="mt-0">
              <ProjectPayments projectId={project.id} />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
